import React, { useEffect, useState, useMemo } from 'react'
import { useHistory } from 'react-router-dom'
import { css } from '@emotion/core'
import { sendMailForTabletCheckout } from '@/apis/aipass'
import { useSelfCheckOutState } from '@/hooks/use-self-check-out-state'
import { FrontTabletLayout } from '@/components/layouts/front-tablet-layout'
import { Header } from '@/components/molecules/front-tablet/header'
import { useTranslation } from 'react-i18next'
import { ButtonType } from '@/components/atoms/button'
import { Body } from '@/components/molecules/front-tablet/body'
import { FrontTabletButton } from '@/components/molecules/front-tablet/button'
import { CheckoutReceipt } from '@/components/organisms/self-checkout/complete/checkout-receipt'
import { CheckoutStatus } from '@/models/self-checkout/setting'
import { usePickByLanguage } from '@/hooks/use-pick-by-language'
import { checkinEposPrinterIdStorage } from '@/constants/self-checkin-device-storage'
import dayjs from 'dayjs'
import { pickUploadReservationUuid } from '@/utils/reservations'

export const Complete: React.FC = () => {
  const history = useHistory()
  const { t } = useTranslation()
  const pickByLanguage = usePickByLanguage()
  const { state, saveState, stateErrorHandling } = useSelfCheckOutState()

  const [isInitializeComponent, setIsInitializeComponent] = useState(false)
  const [isWaitingForConfirm, setIsWaitingForConfirm] = useState(true)

  const existsPrinter: boolean = !!localStorage.getItem(checkinEposPrinterIdStorage)
  // プリンタが存在しない場合、またはプリンタが存在して追加精算がない場合、メール送信のみ
  const isSendMailOnly: boolean = !existsPrinter || (existsPrinter && (state.selectReservation?.totalAmount || 0) === 0)

  // 選択された部屋番号を抽出
  const selectedRoomNumbers = useMemo(() => {
    if (!state.selectReservation) return []

    const selectedIds = state.selectReservation.selectedReservationAndRooms.flatMap(r => r.guestRoomAssignIds)

    return state.selectReservation.selectedReservations
      .flatMap(r => r.assignRooms)
      .filter(room => selectedIds.includes(room.guestRoomAssignId))
      .map(room => room.roomNumber)
      .sort((a, b) => a.localeCompare(b))
  }, [state.selectReservation])

  const goToTopPage = () => {
    history.push({ pathname: `/self-checkin`, search: `?hotelId=${state.hotelId}` })
  }

  const sendMailWithReceipt = async () => {
    await sendMailForTabletCheckout({
      hotelId: state.hotelId,
      paymentResult: {
        receiptName: state.confirm!.receiptName,
        orderId: state.payment!.orderId,
        paymentMethod: state.payment!.paymentMethod,
      },
      checkinId: state.selectReservation!.checkinId,
      reservations: state.selectReservation!.selectedReservationAndRooms,
    })
  }

  const initPageStatus = async () => {
    if (!stateErrorHandling(state, 'complete')) {
      return
    }

    // プリンタが存在しない場合、またはプリンタが存在して追加精算がない場合、メール送信のみ
    if (isSendMailOnly) {
      if (state.payment && !state.completeReceiptHandled) {
        await sendMailWithReceipt()
        saveState({ ...state, completeReceiptHandled: true })
      }
      setIsWaitingForConfirm(false)
    }
    setIsInitializeComponent(true)
  }

  useEffect(() => {
    const timeId = setTimeout(() => goToTopPage(), 60000)
    return () => clearTimeout(timeId)
  }, [])

  useEffect(() => {
    window.scrollTo(0, 0)
    initPageStatus()
  }, [])

  if (!isInitializeComponent) {
    return <></>
  }

  return (
    <FrontTabletLayout state={state}>
      <Header title={t('Check-out completed')} />
      <Body>
        <div css={containerStyle}>
          <img
            className="image-icon"
            src={
              state.complete?.result === CheckoutStatus.FrontSupport
                ? require('@/static/images/image_ng.svg')
                : require('@/static/images/image_ok.svg')
            }
          />
          <div className="title">{t(state.complete?.title || '')}</div>
          <div className="hotel-message">{pickByLanguage(state.complete?.message) || ''}</div>
          <div>
            <FrontTabletButton
              isDisabled={isWaitingForConfirm}
              buttonType={isWaitingForConfirm ? ButtonType.PrimaryDisabled : ButtonType.Primary}
              onClick={goToTopPage}
            >
              {t('SelfCheckIn.Confirmed')}
            </FrontTabletButton>
          </div>
        </div>
        {/* プリンタがある場合、レシートを印刷する */}
        {existsPrinter && !state.completeReceiptHandled && (
          <CheckoutReceipt
            roomNumbers={selectedRoomNumbers}
            checkinId={state.selectReservation?.checkinId || ''}
            selectedReservationAndRooms={state.selectReservation?.selectedReservationAndRooms || []}
            hotelId={state.hotelId}
            totalAmount={state.selectReservation!.totalAmount}
            uploadReservationUuid={pickUploadReservationUuid(state.selectReservation?.selectedReservations, dayjs().format('YYYY-MM-DD'))}
            confirm={state.confirm}
            payment={state.payment}
            onComplete={() => {
              saveState({ ...state, completeReceiptHandled: true })
              setIsWaitingForConfirm(false)
            }}
            onFailed={() => {
              sendMailWithReceipt()
              setIsWaitingForConfirm(false)
            }}
          />
        )}
      </Body>
    </FrontTabletLayout>
  )
}

const containerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 32,
  lineHeight: 1.5,
  paddingTop: 32,
  '.image-icon': { width: 128, height: 128 },
  '.title': { fontSize: 44, fontWeight: 'bold', color: '#F2A40B' },
  '.hotel-message': { fontSize: 30, textAlign: 'center', wordBreak: 'keep-all', overflowWrap: 'anywhere' },
})
